import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, LogOut, Loader2, Save, Image, Plus, Trash2, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, Instagram, Truck, Upload, FolderOpen, BookOpen, Layers } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import AdminSidebar, { AdminLayout } from "../components/admin/AdminSidebar";
import ImageLibraryPicker from "../components/ImageLibraryPicker";
import FocalPointPicker from "../components/FocalPointPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptySlide = () => ({
  id: `slide-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  image_url: "", headline: "", headline_italic: "", subheading: "", launch_text: "", button_text: "Browse Collection", button_link: "/catalog", enabled: true, order: 0
});

const emptyCategory = () => ({
  id: `cat-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  name: "", label: "", image_url: "", enabled: true, order: 0
});

export default function AdminHeroSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [savingSlides, setSavingSlides] = useState(false);
  const [savingCats, setSavingCats] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryTarget, setLibraryTarget] = useState({ type: "slide", index: 0 });
  const [storyImageUrl, setStoryImageUrl] = useState("");
  const [storyPosX, setStoryPosX] = useState(50);
  const [storyPosY, setStoryPosY] = useState(50);
  const [savingStory, setSavingStory] = useState(false);
  const [expandedSlide, setExpandedSlide] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    verify(token);
  }, [navigate]);

  const verify = async (token) => {
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch { localStorage.removeItem("adminToken"); navigate("/admin/login"); }
  };

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } });

  const fetchAll = async () => {
    try {
      const [slidesRes, catsRes, storyRes] = await Promise.all([
        axios.get(`${API}/settings/hero-slides/all`, getAuthHeaders()),
        axios.get(`${API}/settings/categories/all`, getAuthHeaders()),
        axios.get(`${API}/settings/story-image`)
      ]);
      if (slidesRes.data?.slides?.length) setSlides(slidesRes.data.slides);
      else setSlides([emptySlide()]);
      if (catsRes.data?.categories?.length) setCategories(catsRes.data.categories);
      if (storyRes.data?.image_url) setStoryImageUrl(storyRes.data.image_url);
      if (storyRes.data?.position_x !== undefined) setStoryPosX(storyRes.data.position_x);
      if (storyRes.data?.position_y !== undefined) setStoryPosY(storyRes.data.position_y);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSaveSlides = async () => {
    setSavingSlides(true);
    try {
      const ordered = (slides || []).map((s, i) => ({ ...s, order: i }));
      await axios.put(`${API}/settings/hero-slides`, { slides: ordered }, getAuthHeaders());
      toast.success("Hero slides saved!");
    } catch { toast.error("Error saving slides"); }
    finally { setSavingSlides(false); }
  };

  const handleSaveCats = async () => {
    setSavingCats(true);
    try {
      const ordered = (categories || []).map((c, i) => ({ ...c, order: i }));
      await axios.put(`${API}/settings/categories`, { categories: ordered }, getAuthHeaders());
      toast.success("Categories saved!");
    } catch { toast.error("Error saving categories"); }
    finally { setSavingCats(false); }
  };

  const handleSaveStoryImage = async () => {
    setSavingStory(true);
    try {
      await axios.put(`${API}/settings/story-image`, { image_url: storyImageUrl, position_x: storyPosX, position_y: storyPosY }, getAuthHeaders());
      toast.success("Story image saved!");
    } catch { toast.error("Error saving"); }
    finally { setSavingStory(false); }
  };

  const moveSlide = (idx, dir) => {
    const arr = [...slides];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setSlides(arr);
    setExpandedSlide(newIdx);
  };

  const moveCat = (idx, dir) => {
    const arr = [...categories];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setCategories(arr);
  };

  const updateSlide = (idx, field, value) => {
    const arr = [...slides];
    arr[idx] = { ...arr[idx], [field]: value };
    setSlides(arr);
  };

  const updateCat = (idx, field, value) => {
    const arr = [...categories];
    arr[idx] = { ...arr[idx], [field]: value };
    setCategories(arr);
  };

  const handleImageUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } });
      if (target.type === "slide") updateSlide(target.index, "image_url", res.data.url);
      else if (target.type === "category") updateCat(target.index, "image_url", res.data.url);
      else if (target.type === "story") setStoryImageUrl(res.data.url);
      toast.success("Uploaded!");
    } catch { toast.error("Upload failed"); }
  };

  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  const sidebarLinks = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Shirt, label: "Collection", path: "/admin/outfits" },
    { icon: CalendarDays, label: "Bookings", path: "/admin/bookings" },
    { icon: Image, label: "Media Library", path: "/admin/upload" },
    { icon: Layers, label: "Hero & Categories", path: "/admin/hero-settings" },
    { icon: Instagram, label: "Social", path: "/admin/social" },
    { icon: Truck, label: "Delivery", path: "/admin/delivery-settings" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
  ];

  if (loading) return <AdminLayout><AdminLoadingState message="Loading hero settings..." /></AdminLayout>;

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex">
      <AdminSidebar />

      <div className="lg:ml-64 p-6 md:p-10">
        <h1 className="text-xl text-[#282828] font-light mb-8">Hero Slides & Categories</h1>

        {/* === HERO SLIDES === */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm text-[#282828] font-medium flex items-center gap-2"><Image className="w-4 h-4 text-[#9A7B4F]" />Hero Carousel Slides</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setSlides([...slides, emptySlide()]); setExpandedSlide(slides.length); }} className="text-xs"><Plus className="w-3 h-3 mr-1" />Add Slide</Button>
              <Button size="sm" onClick={handleSaveSlides} disabled={savingSlides} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-xs" data-testid="save-slides-btn">
                {savingSlides ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Save Slides
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {(slides || []).map((slide, idx) => (
              <div key={slide.id} className="bg-white border border-[#282828]/10">
                {/* Slide header */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpandedSlide(expandedSlide === idx ? -1 : idx)}>
                  <GripVertical className="w-4 h-4 text-[#676664]/30" />
                  <div className="flex-1 flex items-center gap-3">
                    {slide.image_url && <img src={slide.image_url} alt="" className="w-10 h-7 object-cover" />}
                    <span className="text-xs text-[#282828]">Slide {idx + 1}{slide.headline ? `: ${slide.headline}` : ''}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); updateSlide(idx, "enabled", !slide.enabled); }} className={`p-1 ${slide.enabled ? 'text-green-600' : 'text-[#676664]/30'}`} title={slide.enabled ? "Enabled" : "Disabled"}>
                    {slide.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); moveSlide(idx, -1); }} disabled={idx === 0} className="p-1 text-[#676664]/50 hover:text-[#282828] disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveSlide(idx, 1); }} disabled={idx === slides.length - 1} className="p-1 text-[#676664]/50 hover:text-[#282828] disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); if ((slides || []).length > 1) setSlides((slides || []).filter((_, i) => i !== idx)); }} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                {/* Slide expanded */}
                {expandedSlide === idx && (
                  <div className="border-t border-[#282828]/5 px-4 py-4 grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-[10px] text-[#676664] uppercase tracking-wider">Image</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input value={slide.image_url} onChange={(e) => updateSlide(idx, "image_url", e.target.value)} placeholder="Image URL" className="text-xs flex-1" />
                        <label className="cursor-pointer px-3 py-2 bg-[#FAF8F0] text-[#676664] text-xs hover:bg-[#f0ede5] transition-colors">
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, { type: "slide", index: idx })} /><Upload className="w-3.5 h-3.5" />
                        </label>
                        <Button size="sm" variant="outline" onClick={() => { setLibraryTarget({ type: "slide", index: idx }); setShowLibrary(true); }} className="text-xs px-2"><FolderOpen className="w-3.5 h-3.5" /></Button>
                      </div>
                      {slide.image_url && <img src={slide.image_url} alt="" className="mt-2 w-full max-w-xs h-32 object-cover" />}
                      {slide.image_url && (
                        <FocalPointPicker
                          imageUrl={slide.image_url}
                          positionX={slide.image_position_x ?? 50}
                          positionY={slide.image_position_y ?? 50}
                          onChange={(x, y) => { updateSlide(idx, "image_position_x", x); updateSlide(idx, "image_position_y", y); }}
                          className="mt-3"
                        />
                      )}
                    </div>
                    <div><Label className="text-[10px] text-[#676664] uppercase tracking-wider">Headline</Label><Input value={slide.headline} onChange={(e) => updateSlide(idx, "headline", e.target.value)} className="text-xs mt-1" /></div>
                    <div><Label className="text-[10px] text-[#676664] uppercase tracking-wider">Headline Italic</Label><Input value={slide.headline_italic} onChange={(e) => updateSlide(idx, "headline_italic", e.target.value)} className="text-xs mt-1" /></div>
                    <div className="col-span-2"><Label className="text-[10px] text-[#676664] uppercase tracking-wider">Subheading</Label><Textarea value={slide.subheading} onChange={(e) => updateSlide(idx, "subheading", e.target.value)} className="text-xs mt-1" rows={2} /></div>
                    <div><Label className="text-[10px] text-[#676664] uppercase tracking-wider">Launch Text</Label><Input value={slide.launch_text} onChange={(e) => updateSlide(idx, "launch_text", e.target.value)} className="text-xs mt-1" /></div>
                    <div className="flex gap-2">
                      <div className="flex-1"><Label className="text-[10px] text-[#676664] uppercase tracking-wider">Button Text</Label><Input value={slide.button_text} onChange={(e) => updateSlide(idx, "button_text", e.target.value)} className="text-xs mt-1" /></div>
                      <div className="flex-1"><Label className="text-[10px] text-[#676664] uppercase tracking-wider">Button Link</Label><Input value={slide.button_link} onChange={(e) => updateSlide(idx, "button_link", e.target.value)} className="text-xs mt-1" /></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* === CATEGORIES === */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm text-[#282828] font-medium flex items-center gap-2"><Layers className="w-4 h-4 text-[#9A7B4F]" />Homepage Categories</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setCategories([...categories, emptyCategory()])} className="text-xs"><Plus className="w-3 h-3 mr-1" />Add Category</Button>
              <Button size="sm" onClick={handleSaveCats} disabled={savingCats} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-xs" data-testid="save-categories-btn">
                {savingCats ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Save Categories
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {(categories || []).map((cat, idx) => (
              <div key={cat.id} className="bg-white border border-[#282828]/10 px-4 py-3 flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-[#676664]/30 flex-shrink-0" />
                {cat.image_url && <img src={cat.image_url} alt="" className="w-10 h-10 object-cover flex-shrink-0" />}
                <Input value={cat.name} onChange={(e) => updateCat(idx, "name", e.target.value)} placeholder="Category Name" className="text-xs flex-1" />
                <Input value={cat.label} onChange={(e) => updateCat(idx, "label", e.target.value)} placeholder="Label (e.g. Bridal & Festive)" className="text-xs flex-1" />
                <div className="flex items-center gap-1">
                  <label className="cursor-pointer px-2 py-1.5 bg-[#FAF8F0] text-[#676664] text-xs hover:bg-[#f0ede5] transition-colors">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, { type: "category", index: idx })} /><Upload className="w-3 h-3" />
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => { setLibraryTarget({ type: "category", index: idx }); setShowLibrary(true); }} className="px-2 h-8"><FolderOpen className="w-3 h-3" /></Button>
                </div>
                <button onClick={() => updateCat(idx, "enabled", !cat.enabled)} className={`p-1 ${cat.enabled ? 'text-green-600' : 'text-[#676664]/30'}`}>
                  {cat.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="p-1 text-[#676664]/50 disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => moveCat(idx, 1)} disabled={idx === categories.length - 1} className="p-1 text-[#676664]/50 disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => setCategories(categories.filter((_, i) => i !== idx))} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* === OUR STORY IMAGE === */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-[#9A7B4F]" />
            <h2 className="text-sm text-[#282828] font-medium">Our Story Page Image</h2>
          </div>
          <div className="bg-white border border-[#282828]/10 p-4">
            {storyImageUrl && <img src={storyImageUrl} alt="Story" className="w-full max-w-xs h-48 object-cover mb-3" style={{ objectPosition: `${storyPosX}% ${storyPosY}%` }} />}
            <div className="flex items-center gap-2">
              <Input value={storyImageUrl} onChange={(e) => setStoryImageUrl(e.target.value)} placeholder="Image URL" className="text-xs flex-1" />
              <label className="cursor-pointer px-3 py-2 bg-[#FAF8F0] text-[#676664] text-xs hover:bg-[#f0ede5]">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, { type: "story" })} /><Upload className="w-3.5 h-3.5" />
              </label>
              <Button size="sm" variant="outline" onClick={() => { setLibraryTarget({ type: "story" }); setShowLibrary(true); }} className="text-xs px-2"><FolderOpen className="w-3.5 h-3.5" /></Button>
              <Button size="sm" onClick={handleSaveStoryImage} disabled={savingStory} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-xs" data-testid="save-story-image-btn">
                {savingStory ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save
              </Button>
            </div>
            {storyImageUrl && (
              <FocalPointPicker
                imageUrl={storyImageUrl}
                positionX={storyPosX}
                positionY={storyPosY}
                onChange={(x, y) => { setStoryPosX(x); setStoryPosY(y); }}
                className="mt-4"
              />
            )}
          </div>
        </div>
      </div>

      <ImageLibraryPicker
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        multiple={false}
        onSelect={(url) => {
          if (!url) return;
          if (libraryTarget.type === "slide") updateSlide(libraryTarget.index, "image_url", url);
          else if (libraryTarget.type === "category") updateCat(libraryTarget.index, "image_url", url);
          else if (libraryTarget.type === "story") setStoryImageUrl(url);
        }}
      />
    </div>
  );
}
